import type { IntlShape } from 'react-intl'
import type {
  TransportOption,
  TripDay,
  TripItineraryData,
  TripItineraryItem,
  TripItemWithoutDay,
  TripStop,
} from 'tripPlanner/api/types'
import {
  isCustomFlightSavedItemData,
  isFlightCardSavedItemData,
  isFlightSavedItemData,
  isLeCruiseSavedItemData,
  isLeFlightSavedItemData,
} from 'tripPlanner/api/types'
import { buildFlightName } from '../../utils/transportNames'
import { formatDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import {
  getCruiseSavedOfferId,
  isCruiseSavedData,
  isExperienceSavedData,
  isHotelSavedData,
  type ExperienceSavedData,
  type HotelSavedData,
} from 'tripPlanner/utils/savedItemDataGuards'
import { isNumber } from 'lib/maths/mathUtils'
import type {
  TripListFilters,
  TripListGroup,
  TripListLocation,
  TripListOfferTarget,
  TripListPrice,
  TripListRow,
  TripListRowType,
  TripListStatusValue,
  TripListStopHeader,
  TripListSort,
  TripListViewTarget,
} from './tripListTypes'
import { getTripListPlaceCategory, getTripListRowTypeFromPlaceCategory } from './tripListPlaceCategories'
import { getTripListTypeLabel } from './tripListTypeLabels'
import { getGooglePlacesDollarPriceLevel } from './tripListPriceLevels'
import { getTripListDisplayPrice } from './tripListPrices'
import { getTripListStatus } from './tripListStatus'
import { hasStopSavedOrBookedExperiences, hasStopSavedOrBookedHotels } from 'tripPlanner/utils/chatItinerary'
import { getSortedStopDays, getStopTimingSubtitle } from '../stopTimingUtils'

const TRANSPORT_TYPES = new Set(['FLIGHT', 'TRAIN', 'BUS', 'FERRY', 'OTHER_TRANSPORT'])
const HIDDEN_LIST_ITEM_TYPES = new Set(['TRAIN', 'BUS', 'FERRY', 'OTHER_TRANSPORT', 'CAR_RENTAL'])
type SavedLocation = HotelSavedData['location'] | ExperienceSavedData['location']
function getItemNote(item: TripItineraryItem | TripItemWithoutDay): string | undefined {
  return item.notes
}

function getItemTitle(item: TripItineraryItem | TripItemWithoutDay): string {
  const savedItemData = item.savedItemData
  if (item.type !== 'CRUISE' || savedItemData?.source !== 'le_cruise') return item.name

  return item.name === 'Cruise'
    ? savedItemData.shipName ?? savedItemData.cruiseLineName ?? item.name
    : item.name
}

function getTransportNote(option: TransportOption): string | undefined {
  return option.notes
}

function shouldShowDayItem(item: TripItineraryItem): boolean {
  const isMultiDayItem = (item.totalDays ?? 1) > 1 || (item.dayOfStay ?? 1) > 1
  return !isMultiDayItem || item.dayOfStay === 1 || item.isCheckIn === true
}

function shouldShowListItem(item: TripItineraryItem | TripItemWithoutDay): boolean {
  const itemType = String(item.type)
  if (HIDDEN_LIST_ITEM_TYPES.has(itemType)) return false
  if (itemType === 'FLIGHT') return item.isBooked
  return true
}

function shouldShowTransportOption(rowType: TripListRowType, option: TransportOption): boolean {
  if (option.savedItemData?.source === 'chat_transport') return false
  if (rowType === 'transport' || rowType === 'cruise') return true
  if (rowType !== 'flight') return false
  if (isFlightSavedItemData(option.savedItemData) && option.savedItemData.isBooked === true) return true
  return isFlightCardSavedItemData(option.savedItemData)
}

function createLocation(label: string): TripListLocation {
  const normalizedLabel = label.trim()
  return {
    label: normalizedLabel,
    filterValue: normalizedLabel.toLowerCase(),
  }
}

function createOptionalLocation(label: string | undefined): TripListLocation | undefined {
  const normalizedLabel = label?.trim()
  if (!normalizedLabel) return undefined
  return createLocation(normalizedLabel)
}

function formatSavedLocation(location: SavedLocation | undefined): string | undefined {
  return location?.city || location?.country || undefined
}

function formatStopLocation(stop: TripStop): string {
  return stop.placeName || stop.placeCountry || ''
}

function getPlaceSearchViewTarget(title: string, location: TripListLocation): TripListViewTarget {
  return {
    kind: 'placeSearch',
    searchQuery: `${title} ${location.label}`,
    placeName: title,
  }
}

function getViewTarget({
  title,
  location,
  offerTarget,
  placeTarget,
}: {
  title: string
  location: TripListLocation
  offerTarget?: TripListOfferTarget
  placeTarget?: TripListRow['placeTarget']
}): TripListViewTarget {
  if (offerTarget) {
    return {
      kind: 'offer',
      offerId: offerTarget.offerId,
      accommodationPropertyId: offerTarget.accommodationPropertyId,
      offerType: offerTarget.offerType,
      searchParams: offerTarget.searchParams,
    }
  }

  if (placeTarget) {
    return {
      kind: 'place',
      googlePlaceId: placeTarget.googlePlaceId,
      placeName: placeTarget.placeName,
    }
  }

  return getPlaceSearchViewTarget(title, location)
}

function getPlaceTarget(item: TripItineraryItem | TripItemWithoutDay): TripListRow['placeTarget'] {
  const googlePlaceId = item.itemPlaceDetail?.placeId
  if (!googlePlaceId) return undefined
  return { googlePlaceId, placeName: item.name }
}

function getOfferTarget(item: TripItineraryItem | TripItemWithoutDay): TripListOfferTarget | undefined {
  const savedItemData = item.savedItemData

  if (isCruiseSavedData(savedItemData)) {
    return {
      offerId: savedItemData.offerId,
      offerType: 'cruise',
    }
  }

  if (isExperienceSavedData(savedItemData)) {
    return {
      offerId: savedItemData.experienceId,
      offerType: 'experience',
    }
  }

  if (!isHotelSavedData(savedItemData)) return undefined

  const searchParams: TripListOfferTarget['searchParams'] = {}
  const accommodationPropertyId = 'accommodationPropertyId' in savedItemData ? savedItemData.accommodationPropertyId : undefined
  if (savedItemData.checkIn) searchParams.checkIn = savedItemData.checkIn
  if (savedItemData.checkOut) searchParams.checkOut = savedItemData.checkOut

  return {
    offerId: savedItemData.offerId,
    accommodationPropertyId,
    offerType: savedItemData.offerType,
    searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
  }
}

function getItemPrice(item: TripItineraryItem | TripItemWithoutDay): TripListPrice {
  const savedItemData = item.savedItemData

  if (isHotelSavedData(savedItemData)) {
    return {
      price: isNumber(savedItemData.price) ? savedItemData.price : undefined,
      memberPrice: isNumber(savedItemData.memberPrice) ? savedItemData.memberPrice : undefined,
      currency: savedItemData.currencyCode,
      saleUnit: savedItemData.saleUnit,
    }
  }

  if (isExperienceSavedData(savedItemData)) {
    return {
      price: isNumber(savedItemData.price) ? savedItemData.price : undefined,
      memberPrice: isNumber(savedItemData.memberPrice) ? savedItemData.memberPrice : undefined,
      currency: savedItemData.currencyCode,
      saleUnit: 'person',
    }
  }

  return {
    priceLevel: getGooglePlacesDollarPriceLevel(item.itemPlaceDetail?.priceLevel),
  }
}

function getTransportPrice(option: TransportOption): TripListPrice {
  const savedItemData = option.savedItemData

  if (isHotelSavedData(savedItemData)) {
    return {
      price: isNumber(savedItemData.price) ? savedItemData.price : undefined,
      memberPrice: isNumber(savedItemData.memberPrice) ? savedItemData.memberPrice : undefined,
      currency: savedItemData.currencyCode,
      saleUnit: savedItemData.saleUnit,
    }
  }

  if (isExperienceSavedData(savedItemData)) {
    return {
      price: isNumber(savedItemData.price) ? savedItemData.price : undefined,
      memberPrice: isNumber(savedItemData.memberPrice) ? savedItemData.memberPrice : undefined,
      currency: savedItemData.currencyCode,
      saleUnit: 'person',
    }
  }

  const flightSavedItemData = isFlightSavedItemData(savedItemData) ? savedItemData : undefined

  return {
    price: isNumber(flightSavedItemData?.price) ? flightSavedItemData.price : undefined,
    currency: flightSavedItemData?.currencyCode,
    saleUnit: 'person',
  }
}

function getItemSubtitle(item: TripItineraryItem | TripItemWithoutDay): string | undefined {
  const savedItemData = item.savedItemData
  const rowType = getItemRowType(item)

  if (rowType !== 'accommodation' && rowType !== 'experience' && rowType !== 'flight') {
    return undefined
  }

  if (isHotelSavedData(savedItemData)) {
    return savedItemData.packageName ??
      savedItemData.roomTypeName ??
      savedItemData.bookedRooms?.[0]?.roomTypeName ??
      item.description
  }

  if (isExperienceSavedData(savedItemData)) {
    return formatSavedLocation(savedItemData.location) ?? item.itemPlaceDetail?.primaryTypeDisplayName ?? item.description
  }

  return item.itemPlaceDetail?.primaryTypeDisplayName ?? item.description
}

function getItemRowType(item: TripItineraryItem | TripItemWithoutDay): TripListRowType {
  const savedItemData = item.savedItemData
  if (isHotelSavedData(savedItemData)) return 'accommodation'
  if (isExperienceSavedData(savedItemData)) return 'experience'

  const itemType = String(item.type)
  if (itemType === 'ACCOMMODATION') return 'accommodation'
  if (itemType === 'CRUISE') return 'cruise'
  if (itemType === 'EXPERIENCE') return 'experience'
  if (itemType === 'RESTAURANT' || itemType === 'DINING') return 'dining'
  if (TRANSPORT_TYPES.has(itemType)) return itemType === 'FLIGHT' ? 'flight' : 'transport'

  return getTripListRowTypeFromPlaceCategory(getTripListPlaceCategory({
    primaryType: item.itemPlaceDetail?.primaryType,
    types: item.itemPlaceDetail?.types,
    primaryTypeDisplayName: item.itemPlaceDetail?.primaryTypeDisplayName,
  }))
}

function getTransportRowType(option: TransportOption, transportName: string): TripListRowType {
  const savedItemData = option.savedItemData
  const flightSavedItemData = isFlightSavedItemData(savedItemData) ? savedItemData : undefined

  if (isLeCruiseSavedItemData(savedItemData)) {
    return 'cruise'
  }

  if (isFlightCardSavedItemData(savedItemData)) {
    return 'flight'
  }

  const category = getTripListPlaceCategory({
    fallbackText: [
      option.label,
      transportName,
      flightSavedItemData?.airline,
      flightSavedItemData?.flightNumber,
      flightSavedItemData?.departureAirport,
      flightSavedItemData?.arrivalAirport,
    ].filter(Boolean).join(' '),
  })
  if (category === 'flight') return 'flight'

  return 'transport'
}

function getTransportTitle(option: TransportOption, transportName: string): string {
  if (isLeCruiseSavedItemData(option.savedItemData)) {
    return option.name ?? option.savedItemData.shipName ?? option.label ?? transportName
  }

  return buildFlightName(option, option.label || transportName)
}

function getTransportOfferTarget(option: TransportOption): TripListOfferTarget | undefined {
  if (!isLeCruiseSavedItemData(option.savedItemData)) return undefined

  return {
    offerId: option.savedItemData.offerId,
    offerType: 'cruise',
  }
}

function getFareClassLabel(option: TransportOption): string | undefined {
  const savedItemData = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const searchParams = getStringProperty(savedItemData, 'searchParams')
  if (!searchParams) return undefined

  try {
    const parsed: unknown = JSON.parse(searchParams)
    if (!parsed || typeof parsed !== 'object' || !('fareClass' in parsed)) return undefined
    const fareClass = (parsed as Record<string, unknown>).fareClass
    if (typeof fareClass !== 'string' || !fareClass) return undefined
    return `${fareClass.charAt(0).toUpperCase()}${fareClass.slice(1).toLowerCase()}`
  } catch {
    return undefined
  }
}

function getTransportSubtitle(option: TransportOption, transportName: string, intl: IntlShape): string | undefined {
  if (getTransportRowType(option, transportName) !== 'flight') return undefined
  const fareClassLabel = getFareClassLabel(option)
  if (option.returnLeg && fareClassLabel) {
    return intl.formatMessage(
      {
        id: '1OC0AQII7kubZlniPcYf',
        defaultMessage: 'Return · {fareClass}',
        description: 'Subtitle for return flight rows in the trip planner list view',
      },
      { fareClass: fareClassLabel },
    )
  }

  if (fareClassLabel) return fareClassLabel
  return undefined
}

function getStringProperty(value: object | undefined, property: string): string | undefined {
  if (!value || !(property in value)) return undefined
  const propertyValue = (value as Record<string, unknown>)[property]
  return typeof propertyValue === 'string' ? propertyValue : undefined
}

function getFlightDateProperty(value: object | undefined, dateProperty: 'departureDate' | 'arrivalDate', timeProperty: 'departureTime' | 'arrivalTime'): string | undefined {
  return getStringProperty(value, dateProperty) ?? getStringProperty(value, timeProperty)
}

function formatFlightDate(value: string | undefined, intl: IntlShape): string | undefined {
  const datePart = value?.match(/^\d{4}-\d{2}-\d{2}/)?.[0]
  if (!datePart) return undefined

  return formatDayLabel(datePart, intl)
}

function getFlightDateBoundaryLabel(option: TransportOption, intl: IntlShape): string | undefined {
  const savedItemData = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnSavedItemData = isFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined
  const outboundDepartureDate = formatFlightDate(getFlightDateProperty(savedItemData, 'departureDate', 'departureTime'), intl)
  const outboundArrivalDate = formatFlightDate(getFlightDateProperty(savedItemData, 'arrivalDate', 'arrivalTime'), intl)
  const returnDepartureDate = formatFlightDate(getFlightDateProperty(returnSavedItemData, 'departureDate', 'departureTime'), intl)

  if (returnSavedItemData) {
    const startDate = outboundArrivalDate ?? outboundDepartureDate
    if (startDate && returnDepartureDate && startDate !== returnDepartureDate) {
      return `${startDate} - ${returnDepartureDate}`
    }
    return startDate ?? returnDepartureDate
  }

  if (savedItemData?.flightGroupType === 'multi_city') {
    if (outboundDepartureDate && outboundArrivalDate && outboundDepartureDate !== outboundArrivalDate) {
      return `${outboundDepartureDate} - ${outboundArrivalDate}`
    }
    return outboundDepartureDate ?? outboundArrivalDate
  }

  return outboundArrivalDate ?? outboundDepartureDate
}

function getTransportViewTarget(
  option: TransportOption,
  rowType: TripListRowType,
  offerTarget?: TripListOfferTarget,
): TripListViewTarget {
  if (offerTarget) {
    return {
      kind: 'offer',
      offerId: offerTarget.offerId,
      offerType: offerTarget.offerType,
    }
  }

  if (rowType === 'flight') {
    if (isCustomFlightSavedItemData(option.savedItemData)) {
      return { kind: 'flightSearch' }
    }
    if (isFlightCardSavedItemData(option.savedItemData)) {
      return { kind: 'flightDetails' }
    }
    return { kind: 'flightSearch' }
  }

  return { kind: 'none' }
}

function getDayColumnLabel(intl: IntlShape, day?: TripDay): string {
  if (day?.date) return formatDayLabel(day.date, intl)

  if (day) {
    return intl.formatMessage(
      {
        id: 'nKq1zQQctl_T9caEJO6m',
        defaultMessage: 'Day {dayNumber}',
        description: 'Label for a specific day number in the trip planner itinerary',
      },
      { dayNumber: day.dayNumber },
    )
  }

  return intl.formatMessage({
    id: 'O6R6NLzQ-6hZlM_sqtvo',
    defaultMessage: 'To be planned',
    description: 'Date column label for trip planner list rows that are not assigned to a specific day yet',
  })
}

function getTransportLocation(option: TransportOption, rowType: TripListRowType, transportName: string, stop: TripStop): TripListLocation {
  const savedItemData = option.savedItemData
  const flightSavedItemData = isFlightSavedItemData(savedItemData) ? savedItemData : undefined
  const departureAirport = getStringProperty(flightSavedItemData, 'departureAirportName') ?? flightSavedItemData?.departureAirport
  const arrivalAirport = getStringProperty(flightSavedItemData, 'arrivalAirportName') ?? flightSavedItemData?.arrivalAirport

  if (departureAirport && arrivalAirport) {
    const routeSeparator = option.returnLeg ? '⇄' : '→'
    return createLocation(`${departureAirport} ${routeSeparator} ${arrivalAirport}`)
  }

  if (rowType === 'transport') {
    return createLocation(transportName)
  }

  if (isHotelSavedData(savedItemData) || isExperienceSavedData(savedItemData)) {
    return createOptionalLocation(formatSavedLocation(savedItemData.location)) ?? createLocation(formatStopLocation(stop))
  }

  return createLocation(formatStopLocation(stop))
}

function getItemLocation(item: TripItineraryItem | TripItemWithoutDay, stop: TripStop): TripListLocation {
  const savedItemData = item.savedItemData

  if (isHotelSavedData(savedItemData)) {
    return createOptionalLocation(formatSavedLocation(savedItemData.location)) ?? createLocation(formatStopLocation(stop))
  }

  if (isExperienceSavedData(savedItemData)) {
    return createOptionalLocation(formatSavedLocation(savedItemData.location)) ?? createLocation(formatStopLocation(stop))
  }

  return createLocation(formatStopLocation(stop))
}

function getStatusValue(isBooked: boolean, status: TripListStatusValue | undefined): TripListStatusValue {
  return isBooked ? 'BOOKED' : status ?? 'NOT_BOOKED'
}

function getStatus(
  intl: IntlShape,
  rowType: TripListRowType,
  isBooked: boolean,
  isBookable: boolean,
  status: TripListStatusValue | undefined,
) {
  const value = getStatusValue(isBooked, status)
  return getTripListStatus(intl, value, rowType, isBooked, isBookable, !isBooked)
}

function getGroupTitle(intl: IntlShape, stop: TripStop, day?: TripDay): string {
  if (!day) {
    return intl.formatMessage(
      {
        id: 'DDe3x7fD7p2-eLWqeq2C',
        defaultMessage: 'To be planned · {placeName}',
        description: 'Group heading for unplanned itinerary items in the trip planner list view',
      },
      { placeName: stop.placeName },
    )
  }

  return intl.formatMessage(
    {
      id: 'LV82yE9QAcIlSSoP4g6J',
      defaultMessage: '{dayLabel} · {placeName}',
      description: 'Group heading for a day of itinerary rows in the trip planner list view',
    },
    {
      dayLabel: getLocalisedDayLabel(intl, day.dayNumber, day.date),
      placeName: stop.placeName,
    },
  )
}

function getStopHeader(intl: IntlShape, stop: TripStop): TripListStopHeader {
  return {
    id: `stop:${stop.id}`,
    title: formatStopLocation(stop),
    subtitle: getStopTimingSubtitle(intl, stop),
    stop,
  }
}

function getStopSearchActions(stop: TripStop) {
  return {
    showHotels: !hasStopSavedOrBookedHotels(stop),
    showFlights: true,
    showExperiences: !hasStopSavedOrBookedExperiences(stop),
  }
}

function getFirstStopDay(stop: TripStop): TripDay | undefined {
  return getSortedStopDays(stop)[0]
}

function addVisibleCruiseItemOfferIds<T extends TripItineraryItem | TripItemWithoutDay>(
  offerIds: Set<string>,
  items: ReadonlyArray<T>,
  shouldIncludeItem: (item: T) => boolean,
) {
  for (const item of items) {
    if (!shouldIncludeItem(item)) continue

    const offerId = getCruiseSavedOfferId(item.savedItemData)
    if (offerId) offerIds.add(offerId)
  }
}

function shouldIncludeDayCruiseItem(item: TripItineraryItem): boolean {
  return shouldShowListItem(item) && shouldShowDayItem(item)
}

function getVisibleCruiseItemOfferIds(stops: ReadonlyArray<TripStop>): Set<string> {
  const offerIds = new Set<string>()
  for (const stop of stops) {
    for (const day of stop.days ?? []) {
      addVisibleCruiseItemOfferIds(offerIds, day.items ?? [], shouldIncludeDayCruiseItem)
    }

    addVisibleCruiseItemOfferIds(offerIds, stop.itemsWithoutDay ?? [], shouldShowListItem)
  }

  return offerIds
}

export function buildTripListRows(data: TripItineraryData | undefined, intl: IntlShape): Array<TripListRow> {
  if (!data?.stops.length) return []

  const rows: Array<TripListRow> = []
  const visibleCruiseItemOfferIds = getVisibleCruiseItemOfferIds(data.stops)
  const renderedCruiseOfferIds = new Set<string>()
  let originalIndex = 0

  for (const stop of data.stops) {
    const stopLocation = createLocation(formatStopLocation(stop))

    for (const transport of stop.transports ?? []) {
      for (const option of transport.options ?? []) {
        const cruiseOfferId = getCruiseSavedOfferId(option.savedItemData)
        const hasRenderedCruiseOffer = Boolean(cruiseOfferId && (
          visibleCruiseItemOfferIds.has(cruiseOfferId) || renderedCruiseOfferIds.has(cruiseOfferId)
        ))
        if (hasRenderedCruiseOffer) {
          continue
        }

        const rowType = getTransportRowType(option, transport.name)
        const isBooked = isFlightSavedItemData(option.savedItemData) && option.savedItemData.isBooked === true
        if (!shouldShowTransportOption(rowType, option)) {
          continue
        }
        const offerTarget = getTransportOfferTarget(option)
        const isBookable = isLeFlightSavedItemData(option.savedItemData)
        const title = getTransportTitle(option, transport.name)
        const location = getTransportLocation(option, rowType, transport.name, stop)
        rows.push({
          kind: 'transport',
          id: `transport:${option.itemId}`,
          rowType,
          typeLabel: getTripListTypeLabel(intl, rowType),
          title,
          subtitle: getTransportSubtitle(option, transport.name, intl),
          dayLabel: getFlightDateBoundaryLabel(option, intl) ?? getDayColumnLabel(intl),
          location,
          stopLocation,
          note: getTransportNote(option),
          price: getTransportPrice(option),
          status: getStatus(intl, rowType, isBooked, isBookable, option.status),
          isBooked,
          isBookable,
          originalIndex,
          stop,
          viewTarget: getTransportViewTarget(option, rowType, offerTarget),
          option,
          transport,
          transportName: transport.name,
          offerTarget,
        })
        if (cruiseOfferId) renderedCruiseOfferIds.add(cruiseOfferId)
        originalIndex += 1
      }
    }

    for (const day of stop.days ?? []) {
      for (const item of day.items ?? []) {
        if (item.type === 'FLIGHT' && item.savedItemData?.source === 'chat_flight') {
          continue
        }
        if (!shouldShowListItem(item)) {
          continue
        }
        if (!shouldShowDayItem(item)) {
          continue
        }
        const cruiseOfferId = getCruiseSavedOfferId(item.savedItemData)
        if (cruiseOfferId && renderedCruiseOfferIds.has(cruiseOfferId)) {
          continue
        }
        const rowType = getItemRowType(item)
        const offerTarget = getOfferTarget(item)
        const placeTarget = getPlaceTarget(item)
        const location = getItemLocation(item, stop)
        const title = getItemTitle(item)
        const isBooked = item.isBooked
        const isBookable = rowType !== 'cruise' && Boolean(offerTarget)
        rows.push({
          kind: 'item',
          id: `item:${item.id}:day:${day.id}`,
          rowType,
          typeLabel: getTripListTypeLabel(intl, rowType),
          title,
          subtitle: getItemSubtitle(item),
          dayLabel: getDayColumnLabel(intl, day),
          location,
          stopLocation,
          note: getItemNote(item),
          price: getItemPrice(item),
          status: getStatus(intl, rowType, isBooked, isBookable, item.status),
          isBooked,
          isBookable,
          originalIndex,
          stop,
          day,
          viewTarget: getViewTarget({
            title,
            location,
            offerTarget,
            placeTarget,
          }),
          item,
          offerTarget,
          placeTarget,
        })
        if (cruiseOfferId) renderedCruiseOfferIds.add(cruiseOfferId)
        originalIndex += 1
      }
    }

    for (const item of stop.itemsWithoutDay ?? []) {
      if (!shouldShowListItem(item)) {
        continue
      }
      const cruiseOfferId = getCruiseSavedOfferId(item.savedItemData)
      if (cruiseOfferId && renderedCruiseOfferIds.has(cruiseOfferId)) {
        continue
      }
      const rowType = getItemRowType(item)
      const offerTarget = getOfferTarget(item)
      const placeTarget = getPlaceTarget(item)
      const location = getItemLocation(item, stop)
      const title = getItemTitle(item)
      const isBooked = item.isBooked
      const isBookable = rowType !== 'cruise' && Boolean(offerTarget)
      rows.push({
        kind: 'item',
        id: `item:${item.id}:unplanned:${stop.id}`,
        rowType,
        typeLabel: getTripListTypeLabel(intl, rowType),
        title,
        subtitle: getItemSubtitle(item),
        dayLabel: getDayColumnLabel(intl),
        location,
        stopLocation,
        note: getItemNote(item),
        price: getItemPrice(item),
        status: getStatus(intl, rowType, isBooked, isBookable, item.status),
        isBooked,
        isBookable,
        originalIndex,
        stop,
        viewTarget: getViewTarget({
          title,
          location,
          offerTarget,
          placeTarget,
        }),
        item,
        offerTarget,
        placeTarget,
      })
      if (cruiseOfferId) renderedCruiseOfferIds.add(cruiseOfferId)
      originalIndex += 1
    }
  }

  return rows
}

export function hasRenderableTripListContent(rows: ReadonlyArray<TripListRow>, stops: ReadonlyArray<TripStop> | undefined): boolean {
  if (rows.length > 0) return true
  return stops?.some(stop => stop.stopType !== 'ORIGIN') ?? false
}

export function filterTripListRows(rows: Array<TripListRow>, filters: TripListFilters): Array<TripListRow> {
  return rows.filter(row => {
    if (filters.dates.size > 0 && (!row.day?.date || !filters.dates.has(row.day.date))) return false
    if (filters.types.size > 0 && !filters.types.has(row.rowType)) return false
    if (filters.locations.size > 0 && !filters.locations.has(row.stopLocation.filterValue)) return false
    if (filters.statuses.size > 0 && !filters.statuses.has(row.status.value)) return false
    if (filters.bookableOnly && (!row.isBookable || row.isBooked)) return false
    return true
  })
}

function getComparablePrice(row: TripListRow, luxPlusEnabled: boolean): number | undefined {
  return getTripListDisplayPrice(row.price, luxPlusEnabled)
}

export function sortTripListRows(rows: Array<TripListRow>, sort: TripListSort | undefined, luxPlusEnabled: boolean): Array<TripListRow> {
  if (!sort) return rows

  return [...rows].sort((a, b) => {
    let result = 0

    const aPrice = getComparablePrice(a, luxPlusEnabled)
    const bPrice = getComparablePrice(b, luxPlusEnabled)
    if (aPrice === undefined && bPrice !== undefined) result = 1
    else if (aPrice !== undefined && bPrice === undefined) result = -1
    else if (aPrice !== undefined && bPrice !== undefined) {
      result = sort.direction === 'asc' ? aPrice - bPrice : bPrice - aPrice
    }

    if (result === 0) {
      result = a.originalIndex - b.originalIndex
    }

    return result
  })
}

export function groupTripListRows(
  rows: Array<TripListRow>,
  options: { sort?: TripListSort; grouped?: boolean; stops?: ReadonlyArray<TripStop>; showEmptyStops?: boolean },
  intl: IntlShape,
): Array<TripListGroup> {
  if (!options.grouped) {
    return [{
      id: options.sort ? `sorted:${options.sort.field}:${options.sort.direction}` : 'filtered-results',
      title: intl.formatMessage({
        id: 'yTn-fTiN17JPruVKtf0Q',
        defaultMessage: 'Sorted results',
        description: 'Group heading shown when the trip planner list view is globally sorted or filtered',
      }),
      rowMetadataDisplay: 'all',
      rows,
    }]
  }

  const groups = new Map<string, TripListGroup>()
  const groupsByStopId = new Map<number, Array<TripListGroup>>()

  for (const row of rows) {
    const groupId = row.day ? `day:${row.day.id}` : `unplanned:${row.stop.id}`
    const existing = groups.get(groupId)
    if (existing) {
      existing.rows.push(row)
      continue
    }

    const group: TripListGroup = {
      id: groupId,
      title: getGroupTitle(intl, row.stop, row.day),
      subtitle: row.day?.title,
      rowMetadataDisplay: 'first',
      rows: [row],
    }
    groups.set(groupId, group)
    const stopGroups = groupsByStopId.get(row.stop.id) ?? []
    stopGroups.push(group)
    groupsByStopId.set(row.stop.id, stopGroups)
  }

  if (!options.stops) {
    return Array.from(groups.values()).filter(group => group.rows.length > 0)
  }

  const orderedGroups: Array<TripListGroup> = []
  const renderedGroupIds = new Set<string>()

  for (const stop of options.stops) {
    const stopGroups = groupsByStopId.get(stop.id)
    if (!stopGroups?.length) {
      if (stop.stopType === 'ORIGIN') continue
      if (options.showEmptyStops === false) continue

      const stopHeader = getStopHeader(intl, stop)
      const firstDay = getFirstStopDay(stop)
      orderedGroups.push({
        id: firstDay ? `empty-day:${firstDay.id}` : `empty-stop:${stop.id}`,
        title: getGroupTitle(intl, stop, firstDay),
        subtitle: firstDay?.title,
        rowMetadataDisplay: 'first',
        rows: [],
        stopHeader,
        showEmptyPlaceholder: true,
        stopSearchActions: getStopSearchActions(stop),
      })
      continue
    }

    const stopHeader = getStopHeader(intl, stop)
    stopGroups.forEach((group, index) => {
      if (index === 0) {
        group.stopHeader = stopHeader
        group.stopSearchActions = getStopSearchActions(stop)
      }
      orderedGroups.push(group)
      renderedGroupIds.add(group.id)
    })
  }

  for (const group of groups.values()) {
    if (!renderedGroupIds.has(group.id)) orderedGroups.push(group)
  }

  return orderedGroups.filter(group => group.rows.length > 0 || group.showEmptyPlaceholder)
}
